import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import iconDelete from '../../assets/icon_delete.svg';
import iconEditAdmin from '../../assets/edit_admin.svg';
import AppSpinner from '../../components/AppSpinner';

interface CommunityPostData {
  _id: string;
  title: string;
  content: string;
  category: string;
  user: {
    _id: string;
    displayName: string;
  } | null;
  views: number;
  likes: string[];
  comments: any[];
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

const AdminCommunityListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<CommunityPostData[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<CommunityPostData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchCategory, setSearchCategory] = useState<string>('all');
  const [searchType, setSearchType] = useState<string>('all');
  const [keyword, setKeyword] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchPosts = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/community', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setPosts(response.data);
        setFilteredPosts(response.data);
      } catch (err) {
        setError('게시글 목록을 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [isAuthenticated]);

  const handleSearch = () => {
    let result = [...posts];

    if (searchCategory !== 'all') {
      result = result.filter(post => post.category === searchCategory);
    }

    if (keyword) {
      const lowerKeyword = keyword.toLowerCase();
      result = result.filter(post => {
        const titleMatch = post.title?.toLowerCase().includes(lowerKeyword);
        const contentMatch = post.content?.toLowerCase().includes(lowerKeyword);
        const authorMatch = post.user?.displayName?.toLowerCase().includes(lowerKeyword);

        if (searchType === 'title') return titleMatch;
        if (searchType === 'content') return contentMatch;
        if (searchType === 'author') return authorMatch;
        return titleMatch || contentMatch || authorMatch;
      });
    }

    setFilteredPosts(result);
    setCurrentPage(1);
    setSelectedPostIds([]);
  };

  const handleReset = () => {
    setSearchCategory('all');
    setSearchType('all');
    setKeyword('');
    setFilteredPosts(posts);
    setCurrentPage(1);
    setSelectedPostIds([]);
  };

  const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = paginatedPosts.map(post => post._id);
      setSelectedPostIds(prev => Array.from(new Set([...prev, ...allIds])));
    } else {
      const pageIds = paginatedPosts.map(post => post._id);
      setSelectedPostIds(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectPost = (id: string) => {
    setSelectedPostIds(prev =>
      prev.includes(id) ? prev.filter(postId => postId !== id) : [...prev, id]
    );
  };

  const isAllSelected =
    paginatedPosts.length > 0 && paginatedPosts.every(post => selectedPostIds.includes(post._id));

  const handleDeleteSelected = async () => {
    if (selectedPostIds.length === 0) {
      alert('삭제할 게시글을 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedPostIds.length}개의 게시글을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await Promise.all(selectedPostIds.map(id => axios.delete(`/api/community/${id}`)));
      alert('삭제되었습니다.');
      // Refresh
      const remainingPosts = posts.filter(post => !selectedPostIds.includes(post._id));
      setPosts(remainingPosts);
      setFilteredPosts(prev => prev.filter(post => !selectedPostIds.includes(post._id)));
      setSelectedPostIds([]);
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
      console.error(err);
    }
  };

  if (loading) return <AppSpinner fullscreen tip="Loading..." />;
  if (error) return <div className="admin-page">에러: {error}</div>;

  const categories = [
    '모든 사연',
    '연애',
    'LGBT',
    '외모',
    '대인관계/따돌림',
    '정신건강',
    '성생활',
    '취업/진로',
    '학업/고시',
    '이별/이혼',
    '부부관계',
    '펫로스',
    '성범죄',
    '신체건강',
    '육아/출산',
    '직장',
    '금전/사업',
    '자아/성격',
    '가족',
    '기타',
  ];

  return (
    <div className="admin-page">
      <h1>커뮤니티 관리</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>카테고리</label>
          <select value={searchCategory} onChange={e => setSearchCategory(e.target.value)}>
            <option value="all">전체</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>검색</label>
          <div className="search-input-group">
            <select value={searchType} onChange={e => setSearchType(e.target.value)}>
              <option value="all">전체</option>
              <option value="title">제목</option>
              <option value="content">내용</option>
              <option value="author">작성자</option>
            </select>
            <input
              type="text"
              placeholder="검색어를 입력하세요"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>
            검색
          </button>
          <button className="btn-reset" onClick={handleReset}>
            초기화
          </button>
        </div>
      </div>
      <div className="list-info">
        총 게시물 <span className="count">{filteredPosts.length}</span> | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>

      <div className="table-scroll">
        <table className="table community-list-table">
          <colgroup>
            <col width="50px" />
            <col width="5%" />
            <col width="10%" />
            <col width="20%" />
            <col width="10%" />
            <col width="15%" />
            <col width="10%" />
            <col width="10%" />
            <col width="10%" />
            <col width="10%" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <label className="custom-checkbox">
                  <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                  <span className="checkmark"></span>
                </label>
              </th>
              <th>No</th>
              <th>카테고리</th>
              <th>제목</th>
              <th>작성자</th>
              <th>작성일</th>
              <th>조회수</th>
              <th>좋아요</th>
              <th>댓글</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPosts.length > 0 ? (
              paginatedPosts.map((post, index) => (
                <tr key={post._id}>
                  <td>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPostIds.includes(post._id)}
                        onChange={() => handleSelectPost(post._id)}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </td>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>{post.category}</td>
                  <td>
                    <p className="ellipsis-1">{post.title.length > 20 ? post.title.substring(0, 20) + '...' : post.title}</p>
                  </td>
                  <td>{post.user ? post.user.displayName : '(알 수 없음)'}</td>
                  <td>
                    {new Date(post.createdAt).toLocaleDateString()}{' '}
                    {new Date(post.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>{post.views}</td>
                  <td>{post.likes.length}</td>
                  <td>{post.comments.length}</td>
                  <td>
                    <button className="btn-icon view m-auto"  onClick={() => {navigate(`/admin/community/${post._id}`);}} title="상세보기"></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="no-data">
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination">
            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
              <img src={leftArrow1} alt="화살표" />
            </button>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <img src={leftArrow2} alt="화살표" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={currentPage === page ? 'active' : ''}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              <img src={rightArrow2} alt="화살표" />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              <img src={rightArrow1} alt="화살표" />
            </button>
          </div>      

        <div className="bottom-actions">
          <button className="btn-delete-selection" onClick={handleDeleteSelected}>
            <img src={iconDelete} alt="삭제" />
            삭제
          </button>        
        </div>
      </div>
    </div>
  );
};

export default AdminCommunityListPage;
